import { css, CSSObject } from '@emotion/react';
import React from 'react';
import { useSliderContext, useSliderInstanceContext } from './index';
import { breakpoints } from '../tokens';
import { colors } from '../colors';
import ArrowIcon from '../../../images/svg/arrow.svg';
import PointIcon from '../../../images/svg/nav-point.svg';
import { useGridContext } from '../grid/context';

const buttonStyles = css({
    display: 'flex',
    [breakpoints.tb]: {
        display: 'none',
    },
    position: 'relative',
    width: '35px',
    height: '35px',
    alignItems: 'center',
    justifyContent: 'center',
    svg: { position: 'relative', zIndex: 1, width: '13px', height: '13px' },
    transition: '0.3s',
    '&::after': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        display: 'block',
        width: '25px',
        height: '25px',
        backgroundColor: colors.primaryOrange,
        borderRadius: '30px',
        transform: 'translate(-50%, -50%)',
        content: "''",
        transition: '0.3s',
    },
    '&:active:after': {
        backgroundColor: colors.primaryOrangeHover,
    },
});

type GridBp = { query: string; columns: number; gap: number };
type GridBpEntry = [string, GridBp];

const makePageDotVisibilityCss = (
    slideCount: number,
    slidesPerViewMap: Record<string, number>,
    entries: GridBpEntry[],
    infinite: boolean,
): CSSObject => {
    if (infinite) {
        return {};
    }
    const showFirst = (k: number): CSSObject => {
        const K = Math.max(1, Math.ceil(k));
        return {
            '& > button[data-dot]': { display: 'none' },
            [`& > button[data-dot]:nth-of-type(-n+${K})`]: { display: 'inline-flex' },
        };
    };

    const baseSpv = Math.max(1, Object.values(slidesPerViewMap)[0] ?? 1);
    const baseCss: CSSObject = { '&': showFirst(Math.ceil(slideCount / baseSpv)) };

    const mqCss: CSSObject = entries
        .filter(([, bp]) => Boolean(bp?.query))
        .reduce((acc, [token, bp]) => {
            const spv = slidesPerViewMap[token];
            if (!spv) return acc;
            const pages = Math.ceil(slideCount / Math.max(1, spv));
            return {
                ...acc,
                [bp.query]: showFirst(pages),
            };
        }, {} as CSSObject);

    return { ...baseCss, ...mqCss };
};

const makeArrowDisableCss = (
    slideCount: number,
    spvMap: Record<string, number>,
    entries: GridBpEntry[],
    infinite: boolean,
): CSSObject => {
    if (infinite) return {};
    return entries
        .filter(([, bp]) => Boolean(bp?.query))
        .reduce((acc, [token, bp]) => {
            const spv = spvMap[token];
            if (!spv) return acc;
            const pages = Math.max(1, Math.ceil(slideCount / Math.max(1, spv)));
            const lastPage = pages - 1;

            return {
                ...acc,
                [bp.query]: {
                    '&[data-page="0"] [data-prev]': {
                        cursor: 'default',
                        opacity: 0.5,
                        pointerEvents: 'none',
                    },
                    [`&[data-page="${lastPage}"] [data-next]`]: {
                        cursor: 'default',
                        opacity: 0.5,
                        pointerEvents: 'none',
                    },
                },
            };
        }, {} as CSSObject);
};

const Pagination = ({
    small,
    className,
}: { small?: boolean; className?: string }): JSX.Element => {
    const { paginate, page, jumpTo, slideCount } = useSliderContext();
    const { slidesPerViewMap, infinite } = useSliderInstanceContext();
    const { sortedBreakpoints } = useGridContext();

    const dotVisibilityCss = React.useMemo<CSSObject>(
        () => makePageDotVisibilityCss(slideCount, slidesPerViewMap, sortedBreakpoints, !!infinite),
        [infinite, slideCount, slidesPerViewMap, sortedBreakpoints],
    );

    const arrowDisableCss = React.useMemo<CSSObject>(
        () => makeArrowDisableCss(slideCount, slidesPerViewMap, sortedBreakpoints, !!infinite),
        [infinite, slideCount, slidesPerViewMap, sortedBreakpoints],
    );

    return (
        <div
            aria-hidden="true"
            css={{
                position: 'relative',
                zIndex: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                ...arrowDisableCss,
            }}
            className={className}
            data-page={page}
        >
            {!small && (
                <button
                    type="button"
                    aria-label="Previous"
                    data-prev
                    css={buttonStyles}
                    onClick={(): void => paginate(-1)}
                >
                    <ArrowIcon css={{ fill: colors.shadesWhite }} />
                </button>
            )}

            <div
                css={{
                    display: 'flex',
                    padding: small ? '0' : '32px 24px',
                    justifyContent: 'center',
                    alignItems: 'center',
                    ...dotVisibilityCss,
                }}
            >
                {Array.from({ length: slideCount }).map((_, i) => {
                    const isActive = page === i;
                    return (
                        <button
                            // eslint-disable-next-line react/no-array-index-key
                            key={i}
                            data-dot
                            type="button"
                            aria-label={`Go to page ${i + 1}`}
                            aria-current={isActive ? 'page' : undefined}
                            css={{
                                cursor: 'pointer',
                                padding: 5,
                                listStyle: 'none',
                                svg: {
                                    transition: '0.3s',
                                    fill: isActive ? colors.primaryOrange : colors.grey300,
                                    opacity: isActive ? 1 : 0.3,
                                },
                                '&:active svg': {
                                    fill: colors.primaryOrangeHover,
                                    opacity: 1,
                                },
                            }}
                            onClick={(): void => jumpTo(i)}
                        >
                            <PointIcon />
                        </button>
                    );
                })}
            </div>

            {!small && (
                <button
                    type="button"
                    aria-label="Next"
                    data-next
                    css={buttonStyles}
                    onClick={(): void => paginate(1)}
                >
                    <ArrowIcon css={{ fill: colors.shadesWhite, transform: 'rotate(180deg)' }} />
                </button>
            )}
        </div>
    );
};

export default Pagination;
